import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Gauge, Users, GameController, BookOpen, GraduationCap, ChartLineUp,
  Storefront, Buildings, Flag, Scroll, ChartBar, Gear, Bell, Stethoscope,
  Compass, ShieldCheck, ListBullets, MagnifyingGlass, List, X,
  Package, CreditCard, Lightning, CaretDown, ArrowsClockwise, SignOut,
  Megaphone, UsersThree, SquaresFour, ShieldStar
} from '@phosphor-icons/react';
import GlobalContextSwitcher from '../components/GlobalContextSwitcher';
import { NotificationBell } from '../components/NotificationBell';
import { SupportFooter } from '../components/Support';
import { Logo } from '../components/Logo';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';

const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: Gauge, path: '/admin' },
    ],
  },
  {
    label: 'Entities',
    items: [
      { id: 'users', label: 'Users', icon: Users, path: '/admin/users' },
      { id: 'games', label: 'Games', icon: GameController, path: '/admin/games' },
      { id: 'shops', label: 'Shops', icon: Storefront, path: '/admin/shops' },
      { id: 'publishers', label: 'Publishers', icon: Buildings, path: '/admin/publishers' },
    ],
  },
  {
    label: 'Content',
    items: [
      { id: 'rules', label: 'Rules', icon: BookOpen, path: '/admin/rules' },
      { id: 'tutorials', label: 'Tutorials', icon: GraduationCap, path: '/admin/tutorials' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { id: 'campaigns', label: 'Campaigns', icon: Megaphone, path: '/admin/campaigns' },
      { id: 'community', label: 'Community', icon: UsersThree, path: '/admin/community' },
      { id: 'attendance', label: 'Attendance', icon: ShieldCheck, path: '/admin/attendance' },
      { id: 'reports', label: 'Reports', icon: Flag, path: '/admin/reports' },
      { id: 'verification', label: 'Verification', icon: ShieldCheck, path: '/admin/verification' },
    ],
  },
  {
    label: 'System',
    items: [
      { id: 'plans', label: 'Plans', icon: CreditCard, path: '/admin/plans' },
      { id: 'monetization', label: 'Monetization', icon: ChartLineUp, path: '/admin/monetization' },
      { id: 'analytics', label: 'Analytics', icon: ChartBar, path: '/admin/analytics' },
      { id: 'collections', label: 'Collections', icon: SquaresFour, path: '/admin/collections' },
      { id: 'venue-verification', label: 'Verification', icon: ShieldStar, path: '/admin/venue-verification' },
      { id: 'audit', label: 'Audit Logs', icon: Scroll, path: '/admin/audit' },
      { id: 'diagnostics', label: 'Diagnostics', icon: Stethoscope, path: '/admin/diagnostics' },
    ],
  },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useTenant();
  const { logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const allItems = NAV_SECTIONS.flatMap(s => s.items);

  return (
    <div className="min-h-screen bg-[#07070c] flex" data-testid="admin-layout">
      {/* ─── SIDEBAR ──────────────────────────── */}
      <aside className="hidden lg:flex flex-col w-56 bg-[#09090f] border-r border-white/[0.05] sticky top-0 h-screen flex-shrink-0">
        {/* Brand */}
        <div className="px-4 py-3 border-b border-white/[0.05]">
          <div className="flex items-center gap-2">
            <Logo variant="icon" size="sm" clickable={false} />
            <div>
              <p className="text-white font-bold text-[13px] tracking-tight" style={{ fontFamily: 'Unbounded' }}>CyBoard</p>
              <p className="text-[9px] uppercase tracking-widest text-[#EF4444] font-semibold -mt-0.5">Admin Console</p>
            </div>
          </div>
        </div>

        {/* Context Switcher */}
        <div className="px-3 py-2.5 border-b border-white/[0.05]">
          <GlobalContextSwitcher variant="sidebar" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-1">
          {NAV_SECTIONS.map(section => (
            <div key={section.label} className="mb-0.5">
              <p className="text-[9px] uppercase tracking-[0.12em] text-[#333] px-4 pt-3 pb-1 font-semibold select-none">
                {section.label}
              </p>
              {section.items.map(item => (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-2.5 px-4 py-[7px] text-[13px] transition-all ${
                    isActive(item.path)
                      ? 'text-[#EF4444] bg-[#EF4444]/[0.06] border-r-2 border-[#EF4444]'
                      : 'text-[#666] hover:text-[#ccc] hover:bg-white/[0.02]'
                  }`}
                  data-testid={`admin-nav-${item.id}`}
                >
                  <item.icon size={16} weight={isActive(item.path) ? 'duotone' : 'regular'} />
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* Sign Out + Env indicator */}
        <div className="border-t border-white/[0.05]">
          <button
            onClick={logout}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[12px] text-[#555] hover:text-[#EF4444] hover:bg-[#EF4444]/[0.04] transition-colors"
            data-testid="admin-sign-out"
          >
            <SignOut size={14} />
            Sign Out
          </button>
          <div className="px-4 py-2 flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
            <span className="text-[10px] text-[#333] font-mono">production</span>
          </div>
        </div>
      </aside>

      {/* ─── MAIN AREA ────────────────────────── */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* ─── TOP BAR ────────────────────────── */}
        <header className="sticky top-0 z-30 bg-[#09090f]/95 backdrop-blur-sm border-b border-white/[0.05] px-4 lg:px-6 py-2.5">
          <div className="flex items-center gap-3">
            {/* Mobile menu toggle */}
            <button className="lg:hidden text-[#555] hover:text-white" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={20} /> : <List size={20} />}
            </button>

            {/* Search */}
            <div className={`relative flex-1 max-w-md transition-all ${searchFocused ? 'max-w-lg' : ''}`}>
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#333]" />
              <input
                type="text" placeholder="Search users, shops, games..."
                className="w-full bg-[#0e0e16] border border-white/[0.06] rounded-md pl-9 pr-3 py-[6px] text-[13px] text-white focus:border-[#EF4444]/30 focus:outline-none placeholder-[#2a2a2a] transition-colors"
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                data-testid="admin-global-search"
              />
              <kbd className="hidden sm:inline-flex absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-[#333] bg-white/[0.03] px-1.5 py-0.5 rounded border border-white/[0.06]">/</kbd>
            </div>

            {/* Right utilities */}
            <div className="flex items-center gap-2 ml-auto">
              <NotificationBell />
              <div className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-[#EF4444]/[0.06] border border-[#EF4444]/15">
                <ShieldCheck size={13} className="text-[#EF4444]" />
                <span className="text-[11px] text-[#EF4444] font-medium">Admin</span>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Nav — full-screen overlay drawer */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
                onClick={() => setMobileOpen(false)}
                data-testid="admin-mobile-backdrop"
              />
              {/* Slide-down drawer */}
              <motion.div
                initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                className="lg:hidden fixed left-0 right-0 z-40 bg-[#09090f] border-b border-white/[0.08] px-3 pt-3 pb-3 max-h-[calc(100vh-3.5rem)] overflow-y-auto shadow-2xl shadow-black/60"
                style={{ top: '3.5rem' }}
                data-testid="admin-mobile-nav"
              >
                <div className="mb-2"><GlobalContextSwitcher variant="mobile" /></div>
                <div className="grid grid-cols-2 gap-1.5">
                  {allItems.map(item => (
                    <button key={item.id}
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      className={`flex items-center gap-1.5 px-2 py-2.5 rounded-md text-[11px] font-medium transition-colors min-w-0 overflow-hidden ${
                        isActive(item.path) ? 'bg-[#EF4444]/10 text-[#EF4444]' : 'text-[#aaa] hover:text-white active:bg-white/[0.06]'
                      }`}
                      data-testid={`admin-mobile-nav-${item.id}`}>
                      <item.icon size={14} weight={isActive(item.path) ? 'duotone' : 'regular'} className="flex-shrink-0" />
                      <span className="truncate flex-1 min-w-0 text-left">{item.label}</span>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => { logout(); setMobileOpen(false); }}
                  className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-[11px] text-[#EF4444] bg-[#EF4444]/[0.08] hover:bg-[#EF4444]/[0.15] transition-colors"
                  data-testid="admin-mobile-sign-out"
                >
                  <SignOut size={14} /> Sign Out
                </button>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* ─── CONTENT ────────────────────────── */}
        <main className="flex-1 p-3 sm:p-4 lg:p-6 max-w-[1400px] mx-auto w-full overflow-x-hidden">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.12 }}
          >
            <Outlet />
          </motion.div>
          <SupportFooter />
        </main>
      </div>
    </div>
  );
}
